import {inject, Injectable} from '@angular/core';
import {BehaviorSubject, combineLatest, defer, Observable, throwError} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {ClassificationBatchDTO, ProjectFileControllerService} from '../../api';
import {BackgroundProgress, mergeProgress} from '../background-progress.model';
import {StateConfigService} from '../state/state-config.service';

/** Eine Datei innerhalb eines Klassifizierungs-Stapels, den DIESER Tab gestartet hat. */
interface ClassificationJob {
  fileId: string;
  name: string;
  done: boolean;
  /** Projekt der Datei - damit die Badge in dessen Dateiansicht springen kann. */
  projectId: number;
  /**
   * Wann der Job entstand.
   *
   * Notwendig fuer den Abgleich: ein gerade eingereichter Job steht serverseitig vielleicht noch
   * nicht auf PROCESSING (die Antwort auf den POST ist noch unterwegs). Ohne Schonfrist wuerde ein
   * Abgleich, der genau in dieses Fenster faellt, ihn faelschlich verwerfen.
   */
  startedAt: number;
}

/**
 * Verfolgt die Klassifizierung der Dateien eines Uploads.
 *
 * Warum als Stapel und nicht wie vorher pro Datei: das Frontend rief früher direkt nach den Bytes
 * jeder Datei `POST /files/{id}/classify` auf — bei einem Ordner mit ~380 Dateien also ~380
 * zusätzliche Requests **mitten in die noch laufenden Uploads hinein**. Der Netzwerk-Tab lief voll
 * und die Oberfläche ruckelte. Jetzt reicht der UploadService die fertigen Dateien in Blöcken von 50
 * ein — ~8 Requests statt 380, und trotzdem stimmt der Dateistatus schon während des Uploads (er
 * bleibt nur bis zum Ende des eigenen Blocks auf UPLOADING, nicht bis zum Ende des ganzen Uploads).
 *
 * Ein Block ist serverseitig ein eigener Stapel. Für die Anzeige macht das keinen Unterschied: die
 * Zähler werden summiert.
 *
 * Der Fortschritt braucht keine eigene SSE-Meldung: `CLASSIFICATION_COMPLETED` trägt die Datei-ID,
 * und dieser Service kennt die IDs, die er eingereicht hat. Nach einem Reload sind die IDs mit dem
 * Tab verloren — dann liefert {@link resumeOpenBatches} die Zähler vom Server. Das ist auch der
 * Grund, warum es zwei Quellen gibt: eigene Jobs für den laufenden Fall, Server-Zähler für alles,
 * was diesen Tab überlebt hat.
 */
/** Wie oft der lokale Stand gegen den Server geprueft wird, solange etwas offen ist. */
const RECONCILE_INTERVAL_MS = 20000;
/**
 * Wie lange ein Job vor dem ersten Abgleich geschont wird.
 *
 * Deckt die Zeit ab, bis der Server ihn ueberhaupt als laufend fuehrt - und die Verzoegerung des
 * Zustands-Requests selbst.
 */
const RECONCILE_GRACE_MS = 60000;

@Injectable({providedIn: 'root'})
export class ClassificationService {
  private readonly fileController = inject(ProjectFileControllerService);
  private readonly stateConfigService = inject(StateConfigService);

  private readonly jobsSubject = new BehaviorSubject<ClassificationJob[]>([]);
  /** Serverstand offener Stapel, nach batchId — deckt fremde Tabs und den Reload ab. */
  private readonly batchesSubject = new BehaviorSubject<Map<string, ClassificationBatchDTO>>(new Map());
  /** Stapel-IDs, für die dieser Tab eigene Jobs führt (damit nichts doppelt gezählt wird). */
  private readonly ownBatchIds = new Set<string>();
  private reconcileTimer: ReturnType<typeof setInterval> | null = null;

  /** Fortschritt für die Hintergrund-Badge, EIN Eintrag JE PROJEKT (Begründung im `UploadService`). */
  public readonly progressByProject$: Observable<BackgroundProgress[]> = combineLatest([
    this.jobsSubject, this.batchesSubject,
  ]).pipe(
    map(([jobs, batches]) => {
      const foreign = Array.from(batches.entries())
        .filter(([batchId]) => !this.ownBatchIds.has(batchId))
        .map(([, dto]) => dto);

      // Ein Stapel ohne projectId (im DTO optional) landet beim ausgewählten Projekt — mehr lässt
      // sich darüber nicht sagen.
      const projectIds = new Set<number>([
        ...jobs.map(job => job.projectId),
        ...foreign.map(b => b.projectId ?? this.stateConfigService.getProjectId()),
      ]);

      return Array.from(projectIds).map(projectId => {
        const own = jobs.filter(job => job.projectId === projectId);
        const others = foreign.filter(
          b => (b.projectId ?? this.stateConfigService.getProjectId()) === projectId);

        const done = own.filter(job => job.done).length
          + others.reduce((sum, b) => sum + (b.done ?? 0), 0);
        const total = own.length + others.reduce((sum, b) => sum + (b.total ?? 0), 0);

        return {
          total,
          done,
          // Die Klassifizierung meldet keinen Fehlschlag je Datei zurück: eine gescheiterte Datei
          // landet auf Status ERROR und ist in der Dateiliste sichtbar, nicht hier.
          failed: 0,
          active: Math.max(0, total - done),
          currentNames: [],
          failedNames: [],
          projectId,
        };
      });
    }),
  );

  /** Tabweite Zusammenfassung — für alles, was nicht je Projekt gedacht ist. */
  public readonly progress$: Observable<BackgroundProgress> = this.progressByProject$.pipe(
    map(mergeProgress),
  );

  public readonly hasActiveClassifications$: Observable<boolean> = this.progress$.pipe(
    map(progress => progress.active > 0),
  );

  constructor() {
    this.stateConfigService.sseEvents$.subscribe(event => {
      if (event.type === 'CLASSIFICATION_COMPLETED') {
        this.markDone(event.data as string);
      } else if (event.type === 'FILE_DELETION_PROGRESS') {
        // Eine geloeschte Datei wird nie mehr klassifiziert. Ohne das blieb ihr Job offen und die
        // Anzeige stand dauerhaft auf z.B. "170 von 181" - beobachtet, nachdem alles geloescht war.
        const progress = event.data as {fileId?: string, state?: string};
        if (progress?.fileId && progress.state !== 'DELETING') {
          this.dropFile(progress.fileId);
        }
      }
    });

    // Nach einer SSE-Unterbrechung aufholen: in das Fenster gefallene CLASSIFICATION_COMPLETED sind
    // verloren, und ohne Abgleich blieben die betroffenen Dateien fuer immer im Zaehler stehen.
    this.stateConfigService.sseReconnects$.subscribe(() => this.reconcileWithServerState());

    // Jeden neuen Serverstand sofort auswerten, nicht erst im eigenen 20-Sekunden-Takt.
    //
    // Der Grund ist eine beobachtete Abweichung (2026-09-17): der Banner in der Dateiansicht zeigte
    // "2 Dateien werden klassifiziert", die Badge daneben "95 von 100", also 5 offene. Beide sind
    // fuer sich richtig - der Banner liest die Serverliste `runningClassifications`, die Badge ihre
    // eigenen Jobs. Eine Datei, die in einem Endzustand landet, OHNE dass die
    // CLASSIFICATION_COMPLETED-Meldung hier ankommt (verlorenes Ereignis, oder der
    // ProcessingTimeoutSweeper setzt eine verwaiste Datei auf ERROR), verschwindet sofort aus der
    // Serverliste, blieb hier aber bis zum naechsten eigenen Abgleich offen stehen.
    //
    // Die Liste kommt ohnehin staendig frisch herein (die Dateiansicht pollt alle 15 s, die
    // Dokumentenklassen-Seite alle 20 s, und jede SSE-Meldung aktualisiert sie); sie hier
    // mitzulesen kostet keinen einzigen zusaetzlichen Request und laesst beide Zahlen aus
    // derselben Quelle zusammenlaufen.
    this.stateConfigService.runningClassificationsSet$.subscribe(() => this.settleFinishedJobs());
  }

  /**
   * Prueft den lokalen Stand regelmaessig gegen den Server, solange etwas offen ist.
   *
   * Die Wiederverbindung allein genuegt nicht: ein Ereignis kann auch bei stehender Verbindung
   * verloren gehen, und dann bleibt der Zaehler dauerhaft daneben. Beobachtet am 2026-09-07:
   * "300 von 381" in der Badge, waehrend der Server nur noch EINE Datei als laufend fuehrte - die
   * restlichen 80 waren fertig, ihre Meldung war nur nie angekommen. Und nach dem Loeschen aller
   * Dateien standen weiter 11 offene Klassifizierungen da.
   *
   * Der Takt orientiert sich am Fallback-Poll der Dateiansicht (15 s) und laeuft nur, solange es
   * etwas zu pruefen gibt - im Ruhezustand kein einziger Request.
   */
  private ensureReconcileTimer(): void {
    if (this.reconcileTimer !== null) return;

    this.reconcileTimer = setInterval(() => {
      if (!this.hasOpenWork()) {
        this.stopReconcileTimer();
        return;
      }
      this.reconcileWithServerState();
    }, RECONCILE_INTERVAL_MS);
  }

  private stopReconcileTimer(): void {
    if (this.reconcileTimer === null) return;
    clearInterval(this.reconcileTimer);
    this.reconcileTimer = null;
  }

  private hasOpenWork(): boolean {
    return this.jobsSubject.value.some(job => !job.done) || this.batchesSubject.value.size > 0;
  }

  /**
   * Gleicht den lokalen Stand nach einer SSE-Unterbrechung gegen den Server ab.
   *
   * Anders als beim Loeschen braucht es dafuer keine eigene Abfrage: der Stapel fuehrt serverseitig
   * gar keinen Zustand je Datei, die Wahrheit ist der Dateistatus selbst. Und den hat der
   * StateConfigService gerade frisch geholt — `sseReconnects$` feuert erst danach. Was dort nicht
   * mehr als laufende Klassifizierung steht, ist fertig.
   *
   * Die Zaehler der verfolgten (fremden bzw. nach einem Reload aufgegriffenen) Stapel kommen
   * zusaetzlich vom Server, weil fuer die keine lokalen Jobs existieren, aus denen sich rechnen
   * liesse.
   */
  private reconcileWithServerState(): void {
    if (!this.hasOpenWork()) {
      this.stopReconcileTimer();
      return;
    }

    // Erst den Serverzustand holen, dann abgleichen - gegen alte Daten zu pruefen waere schlimmer
    // als nicht zu pruefen. Bei der SSE-Wiederverbindung ist der Stand schon frisch, ein zweiter
    // Aufruf kostet dort nur einen Request und schadet nicht.
    this.stateConfigService.refreshServerState().subscribe({
      next: () => this.applyServerState(),
      error: () => { /* Beim naechsten Takt erneut. */ },
    });
  }

  /**
   * Uebernimmt den frisch geholten Serverzustand.
   *
   * Was der Server nicht mehr als laufende Klassifizierung fuehrt, ist fertig - dessen Meldung ist
   * verloren gegangen. Junge Jobs bleiben geschont, siehe RECONCILE_GRACE_MS.
   */
  private applyServerState(): void {
    this.settleFinishedJobs();
    this.resumeOpenBatches(this.stateConfigService.getProjectId());
  }

  /**
   * Schliesst die Jobs ab, die der Server nicht mehr als laufende Klassifizierung fuehrt.
   *
   * Rein lokal - kein Request. Deshalb darf das an jedem neuen Serverstand haengen und nicht nur am
   * eigenen Takt (s. Konstruktor). Junge Jobs bleiben geschont, siehe RECONCILE_GRACE_MS: ein
   * gerade eingereichter Job steht serverseitig womoeglich noch nicht auf PROCESSING.
   */
  private settleFinishedJobs(): void {
    const now = Date.now();
    let changed = false;
    const jobs = this.jobsSubject.value.map(job => {
      if (job.done) return job;
      if (now - job.startedAt < RECONCILE_GRACE_MS) return job;
      if (this.stateConfigService.isRunningClassification(job.fileId)) return job;
      changed = true;
      return {...job, done: true};
    });
    if (changed) {
      this.jobsSubject.next(jobs);
    }
  }

  /**
   * Reicht einen Block hochgeladener Dateien zur Klassifizierung ein — ein Request.
   *
   * Die lokalen Jobs entstehen sofort, damit die Badge nicht erst mit der Antwort erscheint. Welche
   * Dateien der Server tatsächlich eingereiht hat, sagt die Antwort (`queuedFileIds`): er überspringt
   * Ordner, unbekannte IDs und Dateien, die schon in Arbeit sind. Danach wird die lokale Liste auf
   * genau diese Menge zurechtgeschnitten — sonst wartete die Anzeige auf Dateien, für die nie eine
   * Meldung kommt.
   */
  public classifyUploadedFiles(projectId: number, files: {fileId: string, name: string}[]): void {
    // Wer das Ergebnis nicht braucht, bekommt das alte Verhalten: abschicken und vergessen.
    this.classifyFiles(projectId, files).subscribe({error: () => undefined});
  }

  /**
   * Wie {@link classifyUploadedFiles}, liefert aber die tatsaechlich eingereihten IDs zurueck.
   *
   * Fuer Aufrufer, die danach etwas tun muessen - der Sammel-Neustart in der Dateiansicht setzt die
   * betroffenen Zeilen sofort auf "wird analysiert" und muss dafuer wissen, welche es geworden sind
   * und ob der Request ueberhaupt durchging.
   *
   * {@code defer}, damit die optimistischen Jobs erst beim Abonnieren entstehen und nicht schon beim
   * Zusammenbauen des Observables - sonst haengt die Badge an einer Klassifizierung, die nie
   * abgeschickt wurde.
   */
  public classifyFiles(projectId: number, files: {fileId: string, name: string}[]): Observable<string[]> {
    return defer(() => {
      if (files.length === 0) {
        return new Observable<string[]>(observer => {
          observer.next([]);
          observer.complete();
        });
      }

      // Erledigte Jobs des letzten Laufs zuerst wegraeumen. Sie standen sonst weiter in den
      // Zaehlern: der Abschnitt in der Badge ist zwar ausgeblendet, solange nichts mehr laeuft,
      // beim naechsten Lauf kam er aber mit den alten Zahlen im Ruecken wieder ("849 von 853"
      // statt "0 von 200"). clearFinished() gab es dafuer schon - es rief nur nie jemand auf.
      this.clearFinished();

      const optimistic: ClassificationJob[] = files.map(
        file => ({...file, done: false, projectId, startedAt: Date.now()}));
      this.jobsSubject.next([...optimistic, ...this.jobsSubject.value]);

      this.ensureReconcileTimer();

      return this.fileController.createClassificationBatch(files.map(f => f.fileId), projectId).pipe(
        map(batch => {
          if (batch.batchId) {
            this.ownBatchIds.add(batch.batchId);
          }
          const queued = new Set(batch.queuedFileIds ?? []);
          this.jobsSubject.next(this.jobsSubject.value.filter(job =>
            !optimistic.some(o => o.fileId === job.fileId) || queued.has(job.fileId)));
          return batch.queuedFileIds ?? [];
        }),
        catchError(error => {
          // Ohne Stapel gibt es nichts zu verfolgen - die optimistischen Jobs wieder entfernen,
          // sonst haengt die Anzeige auf einer Klassifizierung, die nie laeuft.
          this.jobsSubject.next(this.jobsSubject.value.filter(job =>
            !optimistic.some(o => o.fileId === job.fileId)));
          return throwError(() => error);
        }),
      );
    });
  }

  /**
   * Offene Stapel vom Server holen - damit die Badge nach einem Reload wieder aufgreift.
   *
   * Nicht nur hinzufuegen, sondern auch aufraeumen: ein Stapel, den der Server nicht mehr als offen
   * fuehrt, ist fertig. Wurde nur gemerged, blieb ein Stapel, dessen Abschlussmeldung in eine
   * SSE-Unterbrechung gefallen war, dauerhaft in der Anzeige stehen. Beschraenkt auf DIESES Projekt,
   * weil die Abfrage auch nur dessen Stapel liefert.
   */
  public resumeOpenBatches(projectId: number): void {
    this.ensureReconcileTimer();
    this.fileController.getActiveClassificationBatches(projectId).subscribe({
      next: batches => {
        const open = batches.filter(batch => batch.batchId && !batch.completedAt);
        const reported = new Set(open.map(batch => batch.batchId));

        const map = new Map<string, ClassificationBatchDTO>();
        for (const [batchId, batch] of this.batchesSubject.value) {
          if (batch.projectId === projectId && !reported.has(batchId)) continue;
          map.set(batchId, batch);
        }
        open.forEach(batch => map.set(batch.batchId!, batch));
        this.batchesSubject.next(map);
      },
      error: () => { /* Best-Effort: ohne die Zaehler fehlt nur die Anzeige. */ },
    });
  }

  /** Räumt erledigte Jobs weg, damit der nächste Upload mit leerer Liste startet. */
  public clearFinished(): void {
    this.jobsSubject.next(this.jobsSubject.value.filter(job => !job.done));
  }

  /**
   * Nimmt eine Datei aus der Verfolgung - sie wird nicht mehr klassifiziert werden.
   *
   * Anders als {@link markDone} zaehlt das die Datei nicht als erledigt, sondern entfernt sie aus
   * der Bezugsmenge: eine geloeschte Datei soll den Fortschritt nicht auf "fertig" schieben, sie
   * gehoert gar nicht mehr dazu. Die Zaehler fremder Stapel bleiben unberuehrt - fuer die weiss
   * dieser Tab nicht, welche Dateien darin stecken.
   */
  private dropFile(fileId: string): void {
    const remaining = this.jobsSubject.value.filter(job => job.fileId !== fileId);
    if (remaining.length !== this.jobsSubject.value.length) {
      this.jobsSubject.next(remaining);
    }
  }

  private markDone(fileId: string): void {
    if (!fileId) return;

    let changed = false;
    const jobs = this.jobsSubject.value.map(job => {
      if (job.fileId === fileId && !job.done) {
        changed = true;
        return {...job, done: true};
      }
      return job;
    });
    if (changed) {
      this.jobsSubject.next(jobs);
    }

    // Fremde bzw. wiederaufgegriffene Stapel haben hier keine Jobs; deren Zähler werden
    // fortgeschrieben, damit die Anzeige auch nach einem Reload vorankommt.
    const batches = new Map(this.batchesSubject.value);
    let batchChanged = false;
    for (const [batchId, dto] of batches) {
      if (this.ownBatchIds.has(batchId)) continue;
      const done = (dto.done ?? 0) + 1;
      if (done >= (dto.total ?? 0)) {
        batches.delete(batchId);
      } else {
        batches.set(batchId, {...dto, done});
      }
      batchChanged = true;
      break;
    }
    if (batchChanged) {
      this.batchesSubject.next(batches);
    }
  }
}
